import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Download, FileJson, FileSpreadsheet, RefreshCw, Filter, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useTenantOverride } from "@/lib/tenant-override";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

type Row = {
  id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  actor_id: string | null;
  tenant_id: string | null;
  metadata: unknown;
  ip_address: string | null;
  created_at: string;
  total_count?: number;
};

function toCSV(rows: Row[], tenantNameById: Record<string, string>): string {
  const header = ["timestamp", "tenant", "actor_id", "action", "entity_type", "entity_id", "ip", "metadata"];
  const escape = (v: unknown) => {
    const s = v == null ? "" : typeof v === "string" ? v : JSON.stringify(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const body = rows.map(r => [
    r.created_at,
    r.tenant_id ? (tenantNameById[r.tenant_id] ?? r.tenant_id) : "",
    r.actor_id ?? "",
    r.action,
    r.entity_type ?? "",
    r.entity_id ?? "",
    r.ip_address ?? "",
    r.metadata ?? {},
  ].map(escape).join(","));
  return [header.join(","), ...body].join("\n");
}

function download(name: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export function AuditLogsPanel() {
  const { isSuperadmin } = useAuth();
  const { activeTenantId, activeTenant, tenants } = useTenantOverride();
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 86400_000);
  const [from, setFrom] = useState(weekAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [search, setSearch] = useState("");
  const [entity, setEntity] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);

  const tenantNameById = useMemo(() => Object.fromEntries(tenants.map(t => [t.id, t.name])), [tenants]);

  async function load(nextPage = page) {
    setLoading(true);
    const { data, error } = await (supabase as any).rpc("search_audit_logs", {
      _tenant_id: activeTenantId,
      _from: new Date(from).toISOString(),
      _to: new Date(new Date(to).getTime() + 86400_000 - 1).toISOString(),
      _search: search.trim() || null,
      _entity: entity.trim() || null,
      _limit: pageSize,
      _offset: nextPage * pageSize,
    });
    setLoading(false);
    if (error) { toast.error("Failed to load audit logs", { description: error.message }); return; }
    const next = (data as Row[] | null) ?? [];
    setRows(next);
    setTotalCount(Number(next[0]?.total_count ?? 0));
    setPage(nextPage);
  }

  useEffect(() => { setPage(0); load(0); /* eslint-disable-next-line */ }, [activeTenantId, pageSize]);

  function exportCsv() {
    if (!rows.length) { toast("Nothing to export"); return; }
    const name = `audit-${activeTenant?.slug ?? "all"}-${from}_to_${to}.csv`;
    download(name, toCSV(rows, tenantNameById), "text/csv;charset=utf-8");
  }
  function exportJson() {
    if (!rows.length) { toast("Nothing to export"); return; }
    const payload = {
      schema_version: 1,
      exported_at: new Date().toISOString(),
      tenant: activeTenant ? { id: activeTenant.id, slug: activeTenant.slug, name: activeTenant.name } : null,
      range: { from, to },
      count: rows.length,
      records: rows,
    };
    const name = `audit-${activeTenant?.slug ?? "all"}-${from}_to_${to}.json`;
    download(name, JSON.stringify(payload, null, 2), "application/json");
  }

  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const fromRow = totalCount === 0 ? 0 : page * pageSize + 1;
  const toRow = Math.min(totalCount, page * pageSize + rows.length);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Filter className="h-4 w-4" /> Audit trail</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {isSuperadmin
              ? `Compliance-ready log for ${activeTenant?.name ?? "your tenant"}. Filter and export as CSV or JSON.`
              : "Read-only compliance log for your tenant. Filter and export for audits."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => load(page)} disabled={loading}><RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
          <Button size="sm" variant="outline" onClick={exportCsv}><FileSpreadsheet className="mr-1 h-3.5 w-3.5" />CSV</Button>
          <Button size="sm" onClick={exportJson}><FileJson className="mr-1 h-3.5 w-3.5" />JSON</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
          <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
          <div className="md:col-span-2"><Label className="text-xs">Search action, entity, metadata</Label><Input placeholder="invite accepted merchant student" value={search} onChange={e => setSearch(e.target.value)} /></div>
          <div><Label className="text-xs">Entity type</Label><Input placeholder="students" value={entity} onChange={e => setEntity(e.target.value)} /></div>
          <div className="flex items-end"><Button size="sm" onClick={() => load(0)} disabled={loading} className="w-full"><Search className="mr-1 h-3.5 w-3.5" />Search</Button></div>
          <div><Label className="text-xs">Rows/page</Label><Input type="number" min={25} max={200} value={pageSize} onChange={e => setPageSize(Math.max(25, Math.min(200, Number(e.target.value) || 50)))} /></div>
        </div>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[170px]">Timestamp</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Actor</TableHead>
                {isSuperadmin && <TableHead>Tenant</TableHead>}
                <TableHead className="text-right">Metadata</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={isSuperadmin ? 6 : 5} className="py-6 text-center text-xs text-muted-foreground">
                  {loading ? "Loading…" : "No events in this range."}
                </TableCell></TableRow>
              )}
              {rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">{format(new Date(r.created_at), "yyyy-MM-dd HH:mm:ss")}</TableCell>
                  <TableCell><Badge variant="secondary" className="font-mono">{r.action}</Badge></TableCell>
                  <TableCell className="text-xs">{r.entity_type ?? "—"}{r.entity_id ? <span className="ml-1 text-muted-foreground">·{r.entity_id.slice(0,8)}</span> : null}</TableCell>
                  <TableCell className="font-mono text-[11px]">{r.actor_id ? r.actor_id.slice(0, 8) : "—"}</TableCell>
                  {isSuperadmin && <TableCell className="text-xs">{r.tenant_id ? (tenantNameById[r.tenant_id] ?? r.tenant_id.slice(0,8)) : "—"}</TableCell>}
                  <TableCell className="text-right"><code className="text-[11px]">{JSON.stringify(r.metadata)}</code></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 text-[11px] text-muted-foreground">
          <span>{totalCount ? `Showing ${fromRow}-${toRow} of ${totalCount} events.` : "No events match these filters."}</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 px-2" disabled={loading || page === 0} onClick={() => load(page - 1)}><ChevronLeft className="h-3.5 w-3.5" />Previous</Button>
            <span>Page {page + 1} / {pageCount}</span>
            <Button size="sm" variant="outline" className="h-7 px-2" disabled={loading || page + 1 >= pageCount} onClick={() => load(page + 1)}>Next<ChevronRight className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
